<template>
    <h2>Umów się na wizytę</h2>
    <div v-if="currentUser && $route.query.serviceid" class="make-an-appointment">
      <h3>Umawiane zwierzę</h3>
      <p>Wybierz swojego zwierzaka, aby zobaczyć dostępne terminy dla wybranej usługi.</p>
      <div class="make-an-appointment__pets" v-if="pets">
        <div
            :key="pet.id"
            v-for="pet in pets"
            class="make-an-appointment__pets__pet-card"
            :class="{'make-an-appointment__pets__pet-card-active': currentPet === pet.id}"
            @click="() => {currentPet = pet.id; getServiceCalendar()}">
          <img :src="pet.photo_url ? pet.photo_url : getImageUrl()" alt="">
          <span
              :style="{textDecoration: currentPet === pet.id ? 'underline' : 'none'}">
            {{ pet.pet_name }}
          </span>
        </div>
      </div>
      <h3>Dostępne terminy</h3>
      <p v-if="currentPet && !closestAppointment" class="no-service-appointments">
        Brak dostępnych terminów
      </p>
      <div v-if="closestAppointment && currentPet" class="closest-appointment-tile" @click="selectClosestAppointment">
        <div class="closest-appointment-tile__content">
          <div class="tile-info">
            <span class="tile-label">Najbliższy dostępny termin:</span>
            <span class="tile-date">{{ formatSelectedDate(closestAppointment.date) }}, godz. {{ closestAppointment.time }}</span>
          </div>
        </div>
        <button class="select-closest-button">Wybierz</button>
      </div>
      <div class="make-an-appointment__calendar" v-if="currentPet">
        <div class="calendar-header">
          <button @click="previousMonth" class="nav-button">
            <img src="@/assets/icons/arrow-left-short.svg" alt="Poprzedni miesiąc">
          </button>
          <h4 class="calendar-month-year">{{ currentMonthYear }}</h4>
          <button @click="nextMonth" class="nav-button">
            <img src="@/assets/icons/arrow-right-short.svg" alt="Następny miesiąc">
          </button>
        </div>
        <div class="calendar-grid">
          <div class="calendar-weekdays">
            <div v-for="day in weekDayNames" :key="day" class="weekday-name">{{ day }}</div>
          </div>
          <div class="calendar-days">
            <div
              v-for="day in calendarDays"
              :key="day.dateString"
              class="calendar-day"
              :class="{
                'calendar-day--other-month': !day.isCurrentMonth,
                'calendar-day--today': day.isToday,
                'calendar-day--has-appointments': day.hasAppointments,
                'calendar-day--selected': day.dateString === selectedDate,
                'calendar-day--past': day.isPast
              }"
              @click="selectDay(day)">
              <div class="day-number">{{ day.day }}</div>
              <div v-if="day.hasAppointments && day.isCurrentMonth" class="day-indicator">
                <span class="appointment-count">{{ day.availableCount }}</span>
              </div>
            </div>
          </div>
        </div>
        <span class="calendar-info">Przesuń kalendarz w prawo lub lewo, aby zobaczyć więcej dni</span>
      </div>
      <div v-else>
        <p>Kalendarz z terminami pojawi się po wybraniu zwierzaka.</p>
      </div>

      <!-- Modal z godzinami -->
      <div v-if="showTimeModal" class="time-modal-overlay" @click="closeTimeModal">
        <div class="time-modal" @click.stop>
          <div class="time-modal__header">
            <h3>{{ formatSelectedDate(selectedDate) }}</h3>
            <button @click="closeTimeModal" class="close-button">&times;</button>
          </div>
          <div class="time-modal__body">
            <div v-if="selectedDayAppointments.length > 0" class="time-slots">
              <button
                v-for="appointment in selectedDayAppointments"
                :key="appointment.id"
                class="time-slot"
                :class="{
                  'time-slot--available': !appointment.user_id,
                  'time-slot--taken': appointment.user_id,
                  'time-slot--selected': currentItemId === appointment.id
                }"
                :disabled="appointment.user_id"
                @click="selectTime(appointment)">
                {{ appointment.time }}
              </button>
            </div>
            <p v-else class="no-appointments">Brak dostępnych terminów w tym dniu</p>
          </div>
        </div>
      </div>

      <div class="make-an-appointment__summary" v-if="currentDate && currentDateTime && currentItemId && currentPet">
        <span><b>wybrane zwierzę: </b>{{pets.find(pet => pet.id === currentPet).pet_name}}</span>
        <span><b>wybrana godzina: </b>{{currentDateTime}}</span>
        <span><b>wybrana data: </b>{{formatSelectedDate(currentDate)}}</span>
        <button @click="makeAnAppointment" class="confirm-button">Umów się</button>
      </div>
    </div>
    <div v-else>
      <h4>Ładowanie...</h4>
    </div>
  </template>
  
  <script>
  import { useMeta } from 'vue-meta';
  import axios from 'axios';
  import authHeader from '@/services/auth-header';
  import {useAuthStore} from "@/stores/auth";
  import {useAppStore} from "@/stores/app";
  export default{
    name:'MakeAppointment',
    setup () {
      useMeta({ title: 'Umów się na wizytę' });
    
    return {
        authStore: useAuthStore(),
        appStore: useAppStore(),
    }
  },
    data(){
      return{
        pets: null,
        currentDate: '',
        currentDateTime: '',
        currentPet: '',
        currentItemId: '',
        currentMonth: new Date().getMonth(),
        currentYear: new Date().getFullYear(),
        weekDayNames: ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb', 'Nd'],
        calendarDays: [],
        allAppointments: [],
        showTimeModal: false,
        selectedDate: '',
        selectedDayAppointments: []
      }
    },
    computed: {
      currentUser() {
          return this.authStore.user;
      },
      currentMonthYear() {
        const monthNames = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
                           'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];
        return `${monthNames[this.currentMonth]} ${this.currentYear}`;
      },
      closestAppointment() {
        if (!this.allAppointments || this.allAppointments.length === 0) return null;

        const now = new Date();
        const available = this.allAppointments.filter(apt => {
          if (apt.user_id) return false;
          const aptDate = new Date(`${apt.date}T${apt.time}`);
          return aptDate > now;
        });

        if (available.length === 0) return null;

        return available.sort((a, b) => {
          const dateA = new Date(`${a.date}T${a.time}`);
          const dateB = new Date(`${b.date}T${b.time}`);
          return dateA - dateB;
        })[0];
      }
    },
    mounted() {
      if (!this.currentUser) {
        this.$router.push('/login');
      }
      else if (this.currentUser.role_id !== 3) {
        this.$router.push('/login');
      }

      if(!this.$route.query.serviceid){
        this.$router.push('/');
      }
      this.getPets()
    },
    methods:{
        getPets(){
          axios.get(`/User/${this.currentUser.id}`).then(res => {
            this.pets = res.data.pets
          })
        },
        getImageUrl() {
            return new URL(`../../assets/photo-placeholder.png`, import.meta.url).href;
        },
        makeAnAppointment(){
          if(this.currentPet){
            const ob = {
              calendar_id: this.currentItemId,
              user_id: this.currentUser.id,
              employee_id: 0,
              pet_id: this.currentPet
            }
            axios.post('/CalendarItemAddUser', ob, authHeader()).then(res => {
              if(String(res.status).charAt(0) === '2'){
                this.appStore.setIsSuccess('Pomyślnie umówiono na wizytę. Sprawdź swoją skrzynkę e-mail')
                this.closeTimeModal()
                setTimeout(() => {
                  this.$router.push('/profile/visits')
                }, 500)
              }
            }).catch(() => {
              this.appStore.setIsAlert('Wystąpił błąd podczas umawiania wizyty')
            })
          }else{
            this.appStore.setIsAlert('podaj, które zwierzę umawiasz')
          }
        },
        getServiceCalendar(){
          axios.get(`/CalendarItemsForService/${this.$route.query.serviceid}`).then(res => {
            this.allAppointments = res.data
            this.generateCalendar()
          }).catch(() => {
            this.appStore.setIsAlert('Wystąpił błąd podczas pobierania kalendarza')
          })
        },
        generateCalendar() {
          const firstDay = new Date(this.currentYear, this.currentMonth, 1)
          const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0)
          const prevLastDay = new Date(this.currentYear, this.currentMonth, 0)

          const firstDayIndex = (firstDay.getDay() + 6) % 7
          const lastDayDate = lastDay.getDate()
          const prevLastDayDate = prevLastDay.getDate()

          const days = []
          const today = new Date()
          today.setHours(0, 0, 0, 0)

          // Poprzedni miesiąc
          for (let i = firstDayIndex; i > 0; i--) {
            const day = prevLastDayDate - i + 1
            const date = new Date(this.currentYear, this.currentMonth - 1, day)
            days.push({
              day,
              dateString: this.formatDateString(date),
              isCurrentMonth: false,
              isToday: false,
              isPast: date < today,
              hasAppointments: false,
              availableCount: 0
            })
          }

          // Obecny miesiąc
          for (let i = 1; i <= lastDayDate; i++) {
            const date = new Date(this.currentYear, this.currentMonth, i)
            const dateString = this.formatDateString(date)
            const dayAppointments = this.allAppointments.filter(apt => apt.date === dateString)
            const availableAppointments = dayAppointments.filter(apt => !apt.user_id)

            days.push({
              day: i,
              dateString,
              isCurrentMonth: true,
              isToday: this.isSameDay(date, today),
              isPast: date < today,
              hasAppointments: availableAppointments.length > 0,
              availableCount: availableAppointments.length
            })
          }

          // Następny miesiąc
          const remainingDays = 42 - days.length
          for (let i = 1; i <= remainingDays; i++) {
            const date = new Date(this.currentYear, this.currentMonth + 1, i)
            days.push({
              day: i,
              dateString: this.formatDateString(date),
              isCurrentMonth: false,
              isToday: false,
              isPast: date < today,
              hasAppointments: false,
              availableCount: 0
            })
          }

          this.calendarDays = days
        },
        formatDateString(date) {
          const year = date.getFullYear()
          const month = String(date.getMonth() + 1).padStart(2, '0')
          const day = String(date.getDate()).padStart(2, '0')
          return `${year}-${month}-${day}`
        },
        isSameDay(date1, date2) {
          return date1.getFullYear() === date2.getFullYear() &&
                 date1.getMonth() === date2.getMonth() &&
                 date1.getDate() === date2.getDate()
        },
        previousMonth() {
          if (this.currentMonth === 0) {
            this.currentMonth = 11
            this.currentYear--
          } else {
            this.currentMonth--
          }
          this.generateCalendar()
        },
        nextMonth() {
          if (this.currentMonth === 11) {
            this.currentMonth = 0
            this.currentYear++
          } else {
            this.currentMonth++
          }
          this.generateCalendar()
        },
        selectDay(day) {
          if (!day.isCurrentMonth || day.isPast || !day.hasAppointments) return

          this.selectedDate = day.dateString
          this.selectedDayAppointments = this.allAppointments.filter(apt => apt.date === day.dateString)
          this.showTimeModal = true
        },
        closeTimeModal() {
          this.showTimeModal = false
          this.selectedDate = ''
          this.selectedDayAppointments = []
        },
        selectTime(appointment) {
          if (appointment.user_id) return

          this.currentDate = appointment.date
          this.currentDateTime = appointment.time
          this.currentItemId = appointment.id
        },
        formatSelectedDate(dateString) {
          if (!dateString) return ''
          const date = new Date(dateString + 'T00:00:00')
          const dayNames = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota']
          const monthNames = ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
                             'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia']
          const dayName = dayNames[date.getDay()]
          const day = date.getDate()
          const month = monthNames[date.getMonth()]
          return `${dayName}, ${day} ${month} ${date.getFullYear()}`
        },
        selectClosestAppointment() {
          const apt = this.closestAppointment;
          if (!apt) return;

          // Wybierz termin
          this.currentDate = apt.date;
          this.currentDateTime = apt.time;
          this.currentItemId = apt.id;

          // Zaktualizuj widok kalendarza na miesiąc wybranego terminu
          const selectedDate = new Date(apt.date);
          this.currentMonth = selectedDate.getMonth();
          this.currentYear = selectedDate.getFullYear();
          this.selectedDate = apt.date; // Podświetlenie w kalendarzu

          this.generateCalendar();

          // Opcjonalnie: Przewiń do podsumowania
          setTimeout(() => {
            const summary = document.querySelector('.make-an-appointment__summary');
            if (summary) summary.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
    }
  }
  </script>
  
<style scoped lang='scss'>
    .make-an-appointment{
        display: flex;
        flex-direction: column;
        align-items: center;
        max-width: 1200px;
        margin: 0 auto;
        padding: 20px;

        h2, h3 {
          margin-bottom: 15px;
        }

        p {
          margin-bottom: 20px;
          color: #666;
        }

        &__calendar{
            width: 100%;
            margin: 30px 0;
            background: #fff;
            border-radius: 12px;
            padding: 20px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        &__pets{
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          justify-content: center;
          margin: 20px 0;

          &__pet-card{
            width: 200px;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 15px;
            border: 2px solid #e0e0e0;
            border-radius: 12px;
            padding: 15px;
            transition: all .2s ease-in-out;
            background: #fff;

            img{
              width: 100px;
              height: 100px;
              object-fit: cover;
              border-radius: 50%;
              border: 3px solid #f0f0f0;
            }

            span{
              font-weight: 500;
              font-size: 16px;
              color: #333;
            }

            &:hover{
              cursor: pointer;
              border-color: #f2790f;
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(242, 121, 15, 0.2);
            }
          }

          &__pet-card-active {
            border: 2px solid #f2790f;
            box-shadow: 0 4px 12px rgba(242, 121, 15, 0.2);

            span {
              color: #f2790f;
            }
          }
        }

        &__summary{
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 15px;
          margin-top: 30px;
          padding: 25px;
          background: #f9f9f9;
          border-radius: 12px;
          border: 2px solid #f2790f;
          width: 100%;
          max-width: 500px;

          span {
            font-size: 16px;
            color: #333;

            b {
              color: #f2790f;
            }
          }

          .confirm-button{
            border: none;
            border-radius: 8px;
            padding: 12px 40px;
            background-color: #f2790f;
            color: white;
            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all .2s ease-in-out;
            margin-top: 10px;

            &:hover {
              background-color: #d66a0a;
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(242, 121, 15, 0.3);
            }

            &:active {
              transform: translateY(0);
            }
          }
        }
    }

    .closest-appointment-tile {
      width: 100%;
      background: #fff;
      border: 2px solid #f2790f;
      border-radius: 12px;
      padding: 15px 25px;
      margin-bottom: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      transition: all 0.2s ease-in-out;
      box-shadow: 0 4px 12px rgba(242, 121, 15, 0.1);

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(242, 121, 15, 0.2);
        background-color: #fffaf5;
      }

      &__content {
        display: flex;
        align-items: center;
        gap: 20px;

        .tile-icon {
          width: 32px;
          height: 32px;
          filter: invert(48%) sepia(85%) saturate(1918%) hue-rotate(3deg) brightness(101%) contrast(94%);
        }

        .tile-info {
          display: flex;
          flex-direction: column;

          .tile-label {
            font-size: 14px;
            color: #666;
          }

          .tile-date {
            font-size: 18px;
            font-weight: 600;
            color: #333;
          }
        }
      }

      .select-closest-button {
        background-color: #f2790f;
        color: white;
        border: none;
        padding: 8px 20px;
        border-radius: 8px;
        font-weight: 600;
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
          background-color: #d66a0a;
        }
      }
    }

    .no-service-appointments {
      width: 100%;
      text-align: center;
      color: #333;
      font-size: 16px;
      margin-bottom: 20px;
      font-weight: 500;
    }

    @media (max-width: 768px) {
      .closest-appointment-tile {
        flex-direction: column;
        gap: 15px;
        text-align: center;

        &__content {
          flex-direction: column;
          gap: 10px;
        }

        .select-closest-button {
          width: 100%;
        }
      }
    }

    .calendar-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 25px;
      padding-bottom: 20px;
      border-bottom: 2px solid #f0f0f0;

      .calendar-month-year {
        font-size: 24px;
        font-weight: 600;
        color: #333;
        margin: 0;
      }

      .nav-button {
        border: none;
        background-color: #f2790f;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        border-radius: 50%;
        cursor: pointer;
        transition: all .2s ease-in-out;

        img {
          width: 24px;
          filter: brightness(0) invert(1);
        }

        &:hover {
          background-color: #d66a0a;
          transform: scale(1.1);
        }

        &:active {
          transform: scale(0.95);
        }
      }
    }

    .calendar-grid {
      width: 100%;
      overflow-x: auto;
    }

    .calendar-weekdays {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 8px;
      margin-bottom: 10px;

      .weekday-name {
        text-align: center;
        font-weight: 600;
        color: #666;
        padding: 10px;
        font-size: 14px;
      }
    }

    .calendar-days {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 8px;

      .calendar-day {
        aspect-ratio: 1;
        min-height: 80px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 8px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        cursor: pointer;
        transition: all .2s ease-in-out;
        background: #fff;
        position: relative;

        .day-number {
          font-size: 16px;
          font-weight: 500;
          color: #333;
          margin-bottom: 4px;
        }

        .day-indicator {
          margin-top: auto;

          .appointment-count {
            background-color: #f2790f;
            color: white;
            border-radius: 12px;
            padding: 3px 8px;
            font-size: 12px;
            font-weight: 600;
          }
        }

        &--other-month {
          opacity: 0.3;
          cursor: default;

          &:hover {
            border-color: #e0e0e0;
            transform: none;
          }
        }

        &--today {
          border: 2px solid #f2790f;

          .day-number {
            color: #f2790f;
            font-weight: 700;
          }
        }

        &--has-appointments {
          &:hover {
            border-color: #f2790f;
            transform: scale(1.05);
            box-shadow: 0 4px 12px rgba(242, 121, 15, 0.15);
          }
        }

        &--selected {
          background-color: #fff5ed;
          border-color: #f2790f;
        }

        &--past {
          cursor: default;
          opacity: 0.5;

          &:hover {
            border-color: #e0e0e0;
            transform: none;
          }
        }
      }
    }

    .time-modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      animation: fadeIn 0.2s ease-in-out;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .time-modal {
      background: white;
      border-radius: 16px;
      max-width: 500px;
      width: 90%;
      max-height: 80vh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      animation: slideUp 0.3s ease-out;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
    }

    @keyframes slideUp {
      from {
        transform: translateY(50px);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    .time-modal__header {
      padding: 25px;
      border-bottom: 2px solid #f0f0f0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: linear-gradient(135deg, #f2790f 0%, #d66a0a 100%);
      color: white;

      h3 {
        margin: 0;
        font-size: 20px;
        font-weight: 600;
      }

      .close-button {
        background: rgba(255, 255, 255, 0.2);
        border: none;
        font-size: 32px;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        cursor: pointer;
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
        transition: all .2s ease-in-out;

        &:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: rotate(90deg);
        }
      }
    }

    .time-modal__body {
      padding: 25px;
      overflow-y: auto;

      .time-slots {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
        gap: 12px;
      }

      .time-slot {
        padding: 12px 16px;
        border-radius: 8px;
        border: 2px solid #e0e0e0;
        background: white;
        cursor: pointer;
        font-size: 15px;
        font-weight: 500;
        transition: all .2s ease-in-out;

        &--available {
          border-color: #f2790f;
          color: #f2790f;

          &:hover {
            background-color: #f2790f;
            color: white;
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(242, 121, 15, 0.3);
          }
        }

        &--selected {
          background-color: #f2790f;
          color: white;
          border-color: #f2790f;
        }

        &--taken {
          background-color: #f0f0f0;
          color: #999;
          border-color: #e0e0e0;
          text-decoration: line-through;
          cursor: not-allowed;
        }
      }

      .no-appointments {
        text-align: center;
        color: #999;
        padding: 40px 20px;
        font-size: 16px;
      }
    }

    .calendar-info {
      display: none;
      font-size: 12px;
      color: #666;
      margin-top: 10px;
      text-align: right;
    }

    @media (max-width: 768px) {
      .calendar-days .calendar-day {
        min-height: 40px;
        padding: 4px;

        .day-number {
          font-size: 14px;
        }

        .appointment-count {
          font-size: 10px;
          padding: 2px 6px;
        }
      }

      .calendar-weekdays .weekday-name {
        font-size: 12px;
        padding: 6px;
        min-height: 40px;
        min-width: 40px;
      }

      .calendar-info {
        display: block;
      }

      .calendar-header .calendar-month-year {
        font-size: 18px;
      }

      .time-modal__body .time-slots {
        grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
      }
    }
  </style>